// @flow
import React, { Fragment } from 'react';
import { Form, useFormikContext } from 'formik';
import classNames from 'classnames';

import { t } from 'Src/i18n';
import { type FormFieldOption } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { Button } from 'Components/frameworks/button/Button';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { FormikInput as Field } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { FORM_STATUS } from './productQuotationForm.utils';
import { QUOTATION_FORM_ID } from './productQuotationForm.constants';

type NewsletterOptin = null | {
  title: string,
  description: string,
};

export type ProductQuotationFormProps = {
  adultsOptions: $ReadOnlyArray<FormFieldOption>,
  ageIndicationAdults: string,
  ageIndicationKids: string,
  availableDatesIndication: string,
  civilityOptions: $ReadOnlyArray<FormFieldOption>,
  dateFormat: string,
  departureCityOptions: $ReadOnlyArray<FormFieldOption>,
  departureDateOptions: $ReadOnlyArray<FormFieldOption>,
  departureDateEnd: Date,
  departureDateStart: Date,
  extensionOptions: $ReadOnlyArray<FormFieldOption>,
  footnotes: $ReadOnlyArray<string>,
  isEscapade: boolean,
  isSummerCamp: boolean,
  kidsOptions: $ReadOnlyArray<FormFieldOption>,
  newsletter: { id: string, description: string },
  newsletterOptins: {
    optinEmail: NewsletterOptin,
    optinPhone: NewsletterOptin,
    optinSms: NewsletterOptin,
    optinProfile: NewsletterOptin,
  },
};

const FormFieldNote = ({ note }: { note: string }) => (
  <span className="mt-1 text-sm text-gray-medium">{note}</span>
);

export function ProductQuotationForm({
  adultsOptions,
  ageIndicationAdults,
  ageIndicationKids,
  availableDatesIndication,
  civilityOptions,
  dateFormat,
  departureCityOptions,
  departureDateOptions,
  departureDateEnd,
  departureDateStart,
  extensionOptions,
  footnotes,
  isEscapade,
  isSummerCamp,
  kidsOptions,
  newsletter,
  newsletterOptins,
}: ProductQuotationFormProps) {
  const { isSubmitting, isValid, status } = useFormikContext();

  const { optinEmail, optinProfile } = newsletterOptins;

  return (
    <Form id={QUOTATION_FORM_ID} data-testid={QUOTATION_FORM_ID}>
      <Title className="mt-8 mb-1 px-2" color={COLORS.GRAY_DARKER} hLevel={2} theme="title-sans-4">
        {t('productQuotationForm.title')}
      </Title>
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('productQuotationForm.criteria.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2 p-2">
          {isEscapade ? (
            <Field
              dataTestId="departure-date-field"
              dateFormat={dateFormat}
              hideErrorMessage
              isRequired
              label={t('productQuotationForm.criteria.dateLabel')}
              maxDate={departureDateEnd}
              minDate={departureDateStart}
              name="departureDate"
              placeholder={t('productQuotationForm.criteria.datePlaceholder')}
              type="datepicker"
            />
          ) : (
            <Fragment>
              <Field
                dataTestId="departure-date-field"
                hideErrorMessage
                isDisabled={departureDateOptions.length === 0}
                isRequired
                label={t('productQuotationForm.criteria.dateLabel')}
                name="departureDate"
                options={departureDateOptions}
                placeholder={t('productQuotationForm.criteria.datePlaceholder')}
                type="select"
              />
              {departureDateOptions.length === 0 && (
                <span className="flex items-center text-sm mt-1 text-blue">
                  <Icon className="rtl:mr-1" name={ICONS.WARNING} width=".75rem" />
                  {availableDatesIndication}
                </span>
              )}
            </Fragment>
          )}
        </div>
        {extensionOptions.length > 0 && (
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="extension-field"
              hideErrorMessage
              isRequired
              label={t('productQuotationForm.criteria.extensionLabel')}
              name="extension"
              options={extensionOptions}
              placeholder={t('productQuotationForm.criteria.extensionPlaceholder')}
              type="select"
            />
          </div>
        )}
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="departure-city-field"
            hideErrorMessage
            isRequired
            label={t('productQuotationForm.criteria.transportationLabel')}
            name="departureCity"
            options={departureCityOptions}
            placeholder={t('productQuotationForm.criteria.transportationPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('productQuotationForm.pax.title')}
      </Title>
      <div className="flex flex-wrap">
        <p className="w-full mt-2 mb-0 px-2 text-base font-bold">
          {t('productQuotationForm.pax.paxLabel')}
          {'*'}
        </p>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="adults-number-field"
            hideErrorMessage
            name="adultsNumber"
            options={adultsOptions}
            type="select"
          />
          <FormFieldNote note={ageIndicationAdults} />
        </div>
        {!isSummerCamp && (
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="kids-number-field"
              hideErrorMessage
              name="kidsNumber"
              options={kidsOptions}
              type="select"
            />
            <FormFieldNote note={ageIndicationKids} />
          </div>
        )}
      </div>
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('productQuotationForm.coordinates.title')}
      </Title>
      <div className="flex flex-wrap">
        <p className="w-full mt-2 mb-0 px-2 text-base font-bold">
          {t('productQuotationForm.coordinates.informationLabel')}
        </p>
        <div className="w-full">
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="traveler-civility-field"
              hideErrorMessage
              isRequired
              name="traveler.civility"
              options={civilityOptions}
              placeholder={t('productQuotationForm.coordinates.civility')}
              type="select"
            />
          </div>
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-firstName-field"
            hideErrorMessage
            isRequired
            name="traveler.firstName"
            placeholder={`${t('productQuotationForm.coordinates.firstName')} *`}
            type="text"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-lastName-field"
            hideErrorMessage
            isRequired
            name="traveler.lastName"
            placeholder={`${t('productQuotationForm.coordinates.lastName')} *`}
            type="text"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-email-field"
            hideErrorMessage
            isRequired
            name="traveler.email"
            placeholder={`${t('productQuotationForm.coordinates.email')} *`}
            type="text"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-phone-field"
            hideErrorMessage
            isRequired
            name="traveler.phone"
            placeholder={`${t('productQuotationForm.coordinates.phoneNumber')} *`}
            type="text"
          />
        </div>
        {optinEmail && (
          <div className="w-full p-2">
            <Field
              className="mb-3"
              dataTestId="optin-email-field"
              label={newsletter.description || optinEmail.title}
              name="optinEmail"
              optimized
              type="checkbox"
            />
            {optinEmail &&
              optinEmail.description && (
                <Text className="mt-4" theme="text-sans-2" text={optinEmail.description} />
              )}
            {optinProfile &&
              optinProfile.description && (
                <Text className="mt-4" theme="text-sans-2" text={optinProfile.description} />
              )}
          </div>
        )}
      </div>
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('productQuotationForm.specialRequest.title')}
      </Title>
      <div className="w-full p-2">
        <Field
          dataTestId="comment-field"
          name="comment"
          placeholder={t('productQuotationForm.specialRequest.placeholder')}
          type="textarea"
        />
      </div>
      <div
        className={classNames('mt-8 px-2', {
          'text-right': status.state === FORM_STATUS.NEW,
          'flex justify-center': status.state !== FORM_STATUS.NEW,
        })}
      >
        {status.state === FORM_STATUS.NEW ? (
          <Fragment>
            <Button
              colorBg={COLORS.BLUE}
              colorBorder={COLORS.BLUE}
              dataTestId="product-quotation-submit"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              type="submit"
            >
              {t('productQuotationForm.quotation.buttonLabel')}
            </Button>
            {footnotes.length > 0 && (
              <div className="leading-normal py-3 max-w-full text-base text-gray-darker">
                {footnotes.map(note => (
                  <p className="my-0" key={note}>
                    {note}
                  </p>
                ))}
              </div>
            )}
          </Fragment>
        ) : (
          <Confirmation {...status} status={status.state} />
        )}
      </div>
    </Form>
  );
}
